/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useContext, useState } from "react";
import PropTypes from "prop-types";
import { withRouter } from "react-router-dom";
import { Button } from "@material-ui/core";
import Page from "components/common_components/Page/Page";
import useStyles from "../useStyles";
import { useDispatch, useSelector } from "react-redux";
import { notifyError } from "redux/actions/Notifications";
import LoadingPage from "components/common_components/LoadingPage";
import ModulesContext from "../ModulesContext";
import Grid from "@material-ui/core/Grid";
import PlayCircleFilledWhiteIcon from "@material-ui/icons/PlayCircleFilledWhite";
import { hasElements } from "utils/arrays";
import Chat from "components/Chat";
import ChatInitializerContext from "components/Chat/ChatInitializerContext";
import AppContext from "AppContext";
import { getAttributes } from "components/Chat/utils";
import { getEnvVar } from "utils/env";

// Twilio conversation states where the client can still chat (closed is terminal/read-only).
const USABLE_STATES = ["active", "inactive"];

function ChatInitializer({ onCancel }) {
    const classes = useStyles();
    const dispatch = useDispatch();
    const { currentFileCase, isLoading, startConversationAsync, clientPhoto } = useContext(ModulesContext);
    const { user } = useSelector((state) => state.Login);
    const [resources, setResources] = useState({ users: [] });
    // Start resolving if there are conversations to materialize, so the UI doesn't flash with
    // incomplete state before the async Twilio load completes
    const [isResolvingConversations, setIsResolvingConversations] = useState(() =>
        hasElements(currentFileCase?.client?.conversations)
    );
    const clientHasConversations = hasElements(currentFileCase.client.conversations);

    const {
        api: {
            Settings: { Users },
        },
        messaging: {
            twilioConversations: { client },
        },
        conversations,
        setConversations,
        changeChatProfile,
    } = useContext(AppContext);

    function getUsers() {
        Users.getAll({
            onSuccess: (data) => {
                setResources((prev) => ({
                    ...prev,
                    users: data,
                }));
            },
            onError: (error) => {
                setResources((prev) => ({
                    ...prev,
                    users: [],
                }));
                dispatch(notifyError(error));
            },
        });
    }

    useEffect(() => {
        getUsers();
        changeChatProfile(getEnvVar("VITE_CHAT_IDENTITY"));

        return () => {
            setResources((prev) => ({
                users: [],
            }));
            changeChatProfile(undefined);
        };
    }, []);

    useEffect(() => {
        if (!currentFileCase) {
            dispatch(notifyError("You have to select a client file first!"));
            onCancel();
        } else {
            const loadClientConversationsAsync = async () => {
                if (!client || !clientHasConversations) {
                    setIsResolvingConversations(false);
                    return;
                }

                // Materialize every backend conversation so we know its real Twilio state (source of
                // truth). Prefer getConversationBySid (sendable when the identity participates) and
                // fall back to peekConversationBySid (read-only) for closed/non-participant ones.
                const missing = currentFileCase.client.conversations.filter(
                    (x) => !conversations.some((c) => c.sid === x.sid)
                );
                if (!hasElements(missing)) {
                    setIsResolvingConversations(false);
                    return;
                }

                try {
                    setIsResolvingConversations(true);
                    const results = await Promise.allSettled(
                        missing.map(async (x) => {
                            try {
                                return await client.getConversationBySid(x.sid);
                            } catch (error) {
                                return await client.peekConversationBySid(x.sid);
                            }
                        })
                    );
                    const responses = results.filter((r) => r.status === "fulfilled" && r.value).map((r) => r.value);
                    const failures = results.filter((r) => r.status === "rejected");
                    if (hasElements(failures)) {
                        console.error("Error loading some client conversations", failures);
                    }
                    if (hasElements(responses)) {
                        setConversations((prev) => {
                            const toAdd = responses.filter((x) => !prev.some((c) => c.sid === x.sid));
                            return [...prev, ...toAdd];
                        });
                    }
                } catch (error) {
                    console.error("Error loading client conversations", error);
                    dispatch(notifyError(error));
                } finally {
                    setIsResolvingConversations(false);
                }
            };
            loadClientConversationsAsync();
        }
    }, [currentFileCase, client]);

    const startNewConversation = () => startConversationAsync(user.id);

    const resolveInternalUserLabel = (email) => {
        const authorUser = resources.users.find((u) => u.email === email);
        if (authorUser) {
            return `${String(authorUser.name).trim()}`;
        }
        return `Unknown User (${email})`;
    };

    const resolveClientUserLabel = () => {
        return currentFileCase.client.fullName;
    };

    const authorLabelResolver = (message) => {
        const { attributes } = message;
        if (attributes && attributes.senderUserEmail) {
            const { senderUserEmail } = attributes;
            return resolveInternalUserLabel(senderUserEmail);
        }
        return resolveClientUserLabel();
    };

    const clientPhotoResolver = (conversation) => {
        const { type } = getAttributes(conversation);

        if (type === "external") {
            return clientPhoto;
        }
    };

    const conversationNameResolver = async (conversation) => {
        const { type, clientName } = getAttributes(conversation);
        return type === "external" ? clientName : conversation.friendlyName;
    };

    // Twilio state is the source of truth. A conversation is "usable" (the client can still chat)
    // when Twilio reports it active or inactive; closed is terminal/read-only.
    const clientSids = (currentFileCase.client.conversations || []).map((c) => c.sid);
    const clientTwilioConvs = conversations.filter((c) => clientSids.includes(c.sid));

    const getActiveConversation = () => {
        return clientTwilioConvs.find((c) => USABLE_STATES.includes(c.state?.current)) || null;
    };

    const getHistoryConversations = () => {
        const active = getActiveConversation();
        return clientTwilioConvs.filter((c) => c.sid !== active?.sid);
    };

    const conversationsGetter = () => {
        const active = getActiveConversation();
        const history = getHistoryConversations();
        return active ? [active, ...history] : history;
    };

    const shownConversations = conversationsGetter();
    const hasUsableActive = Boolean(getActiveConversation());
    const isSingle = shownConversations.length === 1;

    return (
        <Page className={classes.root}>
            {isLoading || isResolvingConversations ? (
                <LoadingPage />
            ) : (
                <>
                    <Grid container spacing={3} style={{ justifyContent: "space-between" }}>
                        <Grid item xs={6}>
                            {!hasUsableActive && (
                                <Button
                                    variant="text"
                                    size="small"
                                    color="primary"
                                    startIcon={<PlayCircleFilledWhiteIcon />}
                                    onClick={startNewConversation}
                                >
                                    Start conversation
                                </Button>
                            )}
                        </Grid>
                        <Grid item xs={12} className="chat-disclaimer-container">
                            <p className="disclaimer">
                                This chat window is not exclusive for this case, it is directly related to the client's phone number,
                                therefore you will see the same conversation in all cases from the same client.
                            </p>
                        </Grid>
                        {hasElements(shownConversations) && (
                            <Grid item xs={12} className="chat-in-grid">
                                <ChatInitializerContext.Provider
                                    value={{ authorLabelResolver, clientPhotoResolver, conversationNameResolver, conversationsGetter }}
                                >
                                    <Chat isSingleConversation={isSingle} />
                                </ChatInitializerContext.Provider>
                            </Grid>
                        )}
                    </Grid>
                </>
            )}
        </Page>
    );
}

ChatInitializer.propTypes = {
    onCancel: PropTypes.func,
};

ChatInitializer.defaultProps = {
    onCancel: () => {},
};

export default withRouter(ChatInitializer);
